/**
 * =============================================================================
 * useContentProtection.ts - Hook per la protezione contenuti del tenant
 * =============================================================================
 *
 * Legge `tenants.config.content_protection` e restituisce livello,
 * flag effettivi e disclaimer legale. Usato da:
 *  - <ContentProtectionGate> (applicazione runtime dei deterrenti)
 *  - /admin/protezione-contenuti (UI di configurazione)
 *  - eventuali pulsanti che vogliono autonascondersi (es. esportazione).
 *
 * NOTA SUI PRIVILEGI: Gli amministratori bypassano tutte le restrizioni.
 *
 * @version 1.1.0 - Aggiunto bypass logico per amministratori
 * =============================================================================
 */

import { useTenantConfig } from "@/hooks/useFeatureFlag";
import { useAuth } from "@/hooks/useAuth";
import {
  DEFAULT_CONTENT_PROTECTION,
  resolveProtectionFlags,
  type ContentProtectionConfig,
  type ContentProtectionFlags,
  type ContentProtectionLevel,
} from "@/config/contentProtection";

export interface UseContentProtectionResult {
  level: ContentProtectionLevel;
  flags: ContentProtectionFlags;
  legalNotice: string | null;
}

/**
 * Hook principale: restituisce livello e flag di protezione effettivi.
 * In assenza di configurazione, applica DEFAULT_CONTENT_PROTECTION (L1).
 * Se l'utente è un amministratore, forza tutti i flag a false (protezione L0).
 */
export function useContentProtection(): UseContentProtectionResult {
  const config = useTenantConfig();
  const { hasRoleLevel } = useAuth();
  
  const isAdmin = hasRoleLevel("amministratore");
  const raw = (config?.content_protection as Partial<ContentProtectionConfig> | undefined) || null;

  const level: ContentProtectionLevel =
    (raw?.level as ContentProtectionLevel) || DEFAULT_CONTENT_PROTECTION.level;
    
  // Se è un admin, bypassiamo i flag forzandoli a false (comportamento L0)
  const baseFlags = resolveProtectionFlags(raw);
  const flags = isAdmin 
    ? {
        disableContextMenu: false,
        disableTextSelection: false,
        disableCopy: false,
        disableDrag: false,
        disablePrint: false,
        disableSave: false,
        disableExportCourse: false,
      }
    : baseFlags;

  const legalNotice = raw?.legal_notice ?? DEFAULT_CONTENT_PROTECTION.legal_notice ?? null;

  return { level, flags, legalNotice };
}